import { useEffect } from 'react';

/**
 * The bridge between the AI chat widget and every data-holding page.
 *
 * The assistant mutates data on the SERVER (through /api/chat tools); the
 * open page holds a CLIENT-side copy fetched earlier, which has no way to
 * know. So when a chat reply reports that something changed, the widget
 * emits this browser event, and any page (or hook) that registered its
 * reload function refetches - the board updates moments after the
 * assistant's answer, no manual refresh needed.
 *
 * A window event rather than React context on purpose: the widget and the
 * pages sit in different subtrees, and this stays one import per listener.
 */

export const ASSISTANT_CHANGED_EVENT = 'assistant:data-changed';

/** Fired by the ChatWidget after a reply whose tools changed data. */
export function emitAssistantChange(): void {
  window.dispatchEvent(new Event(ASSISTANT_CHANGED_EVENT));
}

/** Call with your page's reload function; it runs on every assistant change. */
export function useAssistantRefresh(refresh: () => void): void {
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener(ASSISTANT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ASSISTANT_CHANGED_EVENT, handler);
  }, [refresh]);
}
